// Copyright (c) 2024-2026 HiveHQ Technologies Private Limited. All Rights Reserved. Proprietary and Confidential.
import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const IS_PRODUCTION = import.meta.env.PROD;
const IS_DEV = import.meta.env.DEV;

// Initialize Mixpanel only if token exists
if (MIXPANEL_TOKEN) {
  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      autocapture: true,
      record_sessions_percent: IS_PRODUCTION ? 10 : 100, // Lower in production
      debug: IS_DEV, // Enable debug logs in development
      ignore_dnt: false, // Respect Do Not Track
      persistence: 'localStorage',
    });

    if (IS_DEV) {
      console.log('[Mixpanel] Initialized successfully');
    }
  } catch (error) {
    console.error('[Mixpanel] Initialization failed:', error);
  }
}

// Sanitize properties to avoid tracking sensitive data
const sanitizeProperties = (properties: Record<string, unknown>): Record<string, unknown> => {
  const sanitized = { ...properties };

  // Remove or mask sensitive fields
  const sensitiveKeys = ['password', 'token', 'secret', 'api_key', 'credit_card'];

  Object.keys(sanitized).forEach((key) => {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
      delete sanitized[key];
    }

    // Truncate error messages to avoid exposing sensitive info
    if (key === 'error' && typeof sanitized[key] === 'string') {
      sanitized[key] = (sanitized[key] as string).substring(0, 100);
    }
  });

  return sanitized;
};

export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (!MIXPANEL_TOKEN) return;

  try {
    const sanitizedProperties = properties ? sanitizeProperties(properties) : undefined;
    mixpanel.track(eventName, sanitizedProperties as Record<string, any>);

    if (IS_DEV) {
      console.log('[Mixpanel] Event tracked:', eventName, sanitizedProperties);
    }
  } catch (error) {
    console.error('[Mixpanel] Failed to track event:', eventName, error);
  }
};

export const identifyUser = (userId: string, properties?: Record<string, unknown>) => {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.identify(userId);
    if (properties) {
      const sanitizedProperties = sanitizeProperties(properties);
      mixpanel.people.set(sanitizedProperties as Record<string, any>);
    }

    if (IS_DEV) {
      console.log('[Mixpanel] User identified:', userId);
    }
  } catch (error) {
    console.error('[Mixpanel] Failed to identify user:', error);
  }
};

export const resetUser = () => {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.reset();

    if (IS_DEV) {
      console.log('[Mixpanel] User reset');
    }
  } catch (error) {
    console.error('[Mixpanel] Failed to reset user:', error);
  }
};

export const isTrackingEnabled = (): boolean => {
  return !!MIXPANEL_TOKEN;
};

export default mixpanel;
